import { z } from 'zod';

/**
 * Common field definitions for reuse across schemas
 */

// Basic ID schema - UUID format
export const idSchema = z.string().uuid();

// Basic date schema - ISO format
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
  message: 'Date must be in YYYY-MM-DD format'
});

// Basic time schema - 24h format
export const timeSchema = z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/, {
  message: 'Time must be in HH:MM or HH:MM:SS format'
});

// Email schema with validation
export const emailSchema = z.string().email({ message: 'Invalid email address' });

// Phone schema with optional formatting
export const phoneSchema = z.string()
  .regex(/^(\+\d{1,3}[- ]?)?\(?\d{3}\)?[- ]?\d{3}[- ]?\d{4}$/, {
    message: 'Invalid phone number format'
  })
  .optional()
  .or(z.literal(''));

// Money amount in cents (integer)
export const moneySchema = z.number().int().nonnegative();

// US state code
export const usStateSchema = z.enum([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL',
  'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
]);

// Address schema
export const addressSchema = z.object({
  address_line1: z.string().min(1, 'Address is required'),
  address_line2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: usStateSchema,
  zip: z.string().regex(/^\d{5}(-\d{4})?$/, { message: 'Invalid ZIP code' })
});

// Name schema (for consistent name validation)
export const nameSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  preferred_name: z.string().optional()
});

/**
 * Date field transformation utilities for forms
 */

// Convert date string to ISO format for database
export const toISODateString = (dateStr: string): string => {
  try {
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr;
  } catch (e) {
    return dateStr;
  }
};

// Format ISO date to MM/DD/YYYY for display
export const fromISODateString = (isoDateStr: string): string => {
  try {
    const [year, month, day] = isoDateStr.split('-');
    return `${month}/${day}/${year}`;
  } catch (e) {
    return isoDateStr;
  }
};

/**
 * Schema with transforms for date fields
 * Use this for form fields that need to convert between MM/DD/YYYY and YYYY-MM-DD
 */
export const dateFieldSchema = z.string()
  .regex(/^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/, {
    message: 'Date must be in MM/DD/YYYY format'
  })
  .transform(toISODateString);

/**
 * Schema with transforms for currency input fields (dollars to cents)
 */
export const currencyFieldSchema = z.string()
  .regex(/^\$?[\d,]+(\.\d{1,2})?$/, { message: 'Invalid currency format' })
  .transform(value => {
    // Remove $ and commas, then convert to cents
    const dollars = parseFloat(value.replace(/[$,]/g, ''));
    return Math.round(dollars * 100);
  });

/**
 * Create a schema with default values for optional fields
 * @param schema The Zod schema to add defaults to
 * @param defaults Default values for fields
 * @returns A new schema with defaults applied
 */
export function withDefaults<T extends z.ZodTypeAny>(
  schema: T,
  defaults: Partial<z.infer<T>>
): z.ZodEffects<T, z.infer<T>, z.infer<T>> {
  return schema.transform(data => ({
    ...defaults,
    ...data
  }));
} 